import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Briefcase, ChevronLeft, ChevronRight, UserRound } from "lucide-react";
import { api } from "../../api/client";
import { holidayName, isHoliday, useTwHolidays } from "../../lib/twHolidays";
import { QuotePopover } from "./QuotePopover";
import { LEAVE_PERIOD_LABEL, LEAVE_STATUS_LABEL, LEAVE_TYPE_SHORT_LABEL } from "../../api/types";
import type { CalendarEvent, LeavePeriod, LeaveRequest, LeaveStatus } from "../../api/types";

// 請假日曆(第七輪 Task 3)。
//
// 業主原話:「這個日歷要把所有報價單上的專案時間放上來,其實也是跟大家說哪一天
// 公司有什麼事情,你確定你要休假嗎 的意思」。
//
// --------------------------------------------------------------------------
// 兩種東西疊在同一張月曆上,而且不可以只靠顏色分辨
// --------------------------------------------------------------------------
// 專案檔期那一條帶「專案」字樣與單號,假單那一條帶姓名與假別,
// 待核准與已核准各自寫出四個字。色盲、灰階列印、螢幕調過色的人都要分得出來 ——
// 而這張月曆的用途正是讓人在排假之前先看懂「那幾天公司在幹嘛」。
//
// 專案那一條刻意做得比假單重(左側粗邊 + 實心底 + 粗體單號):業主要的是
// 先讓人看到公司那幾天有事,再決定要不要休 —— 只是一塊淡淡的背景色達不到這件事。
//
// --------------------------------------------------------------------------
// 資料從哪裡來
// --------------------------------------------------------------------------
// 檔期只來自 `GET /api/calendar/events`,那一支只回**兩個日期欄都填了**的報價單。
// 前端絕不自己去撈報價單、拿自由文字的「專案時間」猜日期 —— 「12/12 下午 –
// 12/13 全日」猜錯一天,排假的人就看到錯的檔期,那比看不到更糟。
//
// 假單與檔期用**同一個窗口**撈:`GET /api/leave?from=&to=`,範圍就是畫面上那幾格。
// 不可以改成以「游標停在哪一年」撈 —— 月曆的格子會補滿前後月份、跨到另一年
// (2027 年 1 月的格子從 2026-12-28 畫起,2026 年 12 月的格子畫到 2027-01-03),
// 以年度撈的話那幾格的假一筆都不會回來,而同幾格的專案檔期照樣畫得出來,
// 畫面上就變成「那天只有公司的事、沒有人請假」—— 而年底排假正是最需要看清楚
// 那幾天的時候。兩支端點的判定都是重疊(`起日 <= to AND 迄日 >= from`),
// 所以跨月、跨年的那一條在每一張月曆上都完整。

/** 一天的毫秒數。日期一律換算成「從 epoch 起算的第幾天」再算,不碰時區。 */
const MS_PER_DAY = 86400000;

/** 月曆一排七天,**星期日開頭**(業主 2026-10-15,與門口打卡機的日曆一致)。 */
const WEEKDAYS = ["日", "一", "二", "三", "四", "五", "六"];

/**
 * 會畫在月曆上的假單狀態。
 *
 * 已駁回與已取消都不是「請成了」。留在月曆上等於告訴大家那幾天有人不在,
 * 而其實沒有 —— 那正是這張月曆最不該說錯的一件事。
 */
const CALENDAR_STATUSES: LeaveStatus[] = ["PENDING", "APPROVED"];

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/**
 * `"YYYY-MM-DD"` → 從 epoch 起算的第幾天。認不得的字串回 `null`。
 *
 * 一律走 `Date.UTC`:用本地時間解析的話,台北時間半夜的那一格會被算到前一天,
 * 整張月曆跟著位移一天。「2026-02-30」這種寫法 `Date.UTC` 會默默滾到三月,
 * 所以組回去再比一次,滾過的就是不合法。
 */
export function epochDay(iso: string | null | undefined): number | null {
  const matched = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso ?? "").trim());
  if (!matched) return null;
  const year = Number(matched[1]);
  const month = Number(matched[2]);
  const day = Number(matched[3]);
  const stamp = Date.UTC(year, month - 1, day);
  const back = new Date(stamp);
  if (back.getUTCFullYear() !== year || back.getUTCMonth() !== month - 1 || back.getUTCDate() !== day) {
    return null;
  }
  return Math.round(stamp / MS_PER_DAY);
}

/** 從 epoch 起算的第幾天 → `"YYYY-MM-DD"`。 */
export function isoOfEpochDay(epoch: number): string {
  const at = new Date(epoch * MS_PER_DAY);
  return `${at.getUTCFullYear()}-${pad(at.getUTCMonth() + 1)}-${pad(at.getUTCDate())}`;
}

/** 今天,以**本地時間**判定 —— 使用者說的「今天」是他眼前那一天。 */
export function todayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** `"2026-09-14"` → `"9/14"`。月曆與清單都只需要月日。 */
function monthDay(iso: string): string {
  const matched = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso ?? "").trim());
  return matched ? `${Number(matched[2])}/${Number(matched[3])}` : String(iso ?? "");
}

function periodSuffix(period: LeavePeriod): string {
  return period === "FULL" ? "" : ` ${LEAVE_PERIOD_LABEL[period]}`;
}

/** 整點寫成「09:00」。 */
export function clockText(hour: number): string {
  return `${String(hour).padStart(2, "0")}:00`;
}

/** 按小時的假幾點到幾點:「09:00–11:00」;沒記開始時間就是 null。 */
export function clockRange(startHour: number | null | undefined, hours: number): string | null {
  if (startHour == null) return null;
  return `${clockText(startHour)}–${clockText(startHour + hours)}`;
}

/**
 * 一段假的日期寫法:`9/14`、`9/14 上午`、`9/14 下午 – 9/15`。
 *
 * 半天的那一半一定要寫出來 —— 「9/14」與「9/14 上午」在排班上是兩回事,
 * 而它們的差別只有那兩個字。
 */
export function formatLeaveRange(
  row: Pick<LeaveRequest, "start_date" | "start_period" | "end_date" | "end_period" | "hours" | "start_hour">,
): string {
  // 按小時的假(業主 2026-11-03)一定是同一天:「11/5 09:00–11:00(2 小時)」;
  // 11/3 上午那版沒記開始時間的舊單寫「11/5 3 小時」
  if (row.hours != null) {
    const clock = clockRange(row.start_hour, row.hours);
    return clock
      ? `${monthDay(row.start_date)} ${clock}(${row.hours} 小時)`
      : `${monthDay(row.start_date)} ${row.hours} 小時`;
  }
  const start = `${monthDay(row.start_date)}${periodSuffix(row.start_period)}`;
  if (row.start_date === row.end_date) return start;
  return `${start} – ${monthDay(row.end_date)}${periodSuffix(row.end_period)}`;
}

/** 一段專案檔期的日期寫法:`12/12 – 12/13`,單日就只寫一天。 */
function formatEventRange(event: CalendarEvent): string {
  const start = monthDay(event.start_date);
  return event.start_date === event.end_date ? start : `${start} – ${monthDay(event.end_date)}`;
}

interface MonthCursor {
  year: number;
  /** 1~12,不是 JS 的 0~11 —— 畫面上寫的是「9 月」,少一個轉換就少一種錯法。 */
  month: number;
}

function shiftMonth(cursor: MonthCursor, delta: number): MonthCursor {
  const zeroBased = cursor.month - 1 + delta;
  return { year: cursor.year + Math.floor(zeroBased / 12), month: ((zeroBased % 12) + 12) % 12 + 1 };
}

function currentMonth(): MonthCursor {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

/** 這個月的月曆要畫幾格、從哪一天開始(含補滿頭尾那幾天)。 */
function gridOf(cursor: MonthCursor): { start: number; cells: number } {
  const first = Math.round(Date.UTC(cursor.year, cursor.month - 1, 1) / MS_PER_DAY);
  const next = Math.round(Date.UTC(cursor.year, cursor.month, 1) / MS_PER_DAY);
  // getUTCDay() 是週日 0,正好就是星期日開頭的第幾格
  const lead = new Date(first * MS_PER_DAY).getUTCDay();
  const days = next - first;
  return { start: first - lead, cells: Math.ceil((lead + days) / 7) * 7 };
}

/** 這一格那一天要畫的半天標記:`上午`、`下午起`、`上午止`。 */
function halfDayMark(row: LeaveRequest, epoch: number, start: number, end: number): string {
  if (row.hours != null) return clockRange(row.start_hour, row.hours) ?? `${row.hours} 小時`;
  if (start === end) return row.start_period === "FULL" ? "" : LEAVE_PERIOD_LABEL[row.start_period];
  if (epoch === start && row.start_period === "PM") return "下午起";
  if (epoch === end && row.end_period === "AM") return "上午止";
  return "";
}

/**
 * 一格有多高、要不要把每一件事的字畫出來(業主 2026-09-17:
 * 「日歷做成3種大小 大中小 資訊大的最清楚 以此類推」)。
 *
 * **小的那一種不畫字,只畫數量。** 硬把同樣的字塞進一格高的格子裡,
 * 出來的是一排看不懂的碎字 —— 那比只寫「3 件」更難用。
 */
export type CalendarSize = "sm" | "md" | "lg";

/** 格(月曆)或列(一條一條)。業主:「也保留目前的顯示方式 格/列(目前的)」。 */
export type CalendarView = "grid" | "list";

const CELL_HEIGHT: Record<CalendarSize, string> = {
  sm: "h-12",
  md: "h-24",
  lg: "h-36",
};

/** 撐滿外框時每一格的最低高度:只保底放得下日期,其餘高度由外框分配。 */
const FILL_CELL_HEIGHT = "h-8";

const SIZE_LABEL: { value: CalendarSize; label: string }[] = [
  { value: "sm", label: "小" },
  { value: "md", label: "中" },
  { value: "lg", label: "大" },
];

export interface LeaveCalendarProps {
  /** 額外的樣式,交給呼叫端排版。 */
  className?: string;
  /**
   * 這張月曆叫什麼。
   *
   * 請假頁是「休假日曆」,首頁是「日曆」——業主:「不要叫做我接下來的檔期
   * 就是日歷 就好」。同一個元件,兩個地方的名字不一樣,所以名字由呼叫端給。
   */
  label?: string;
  size?: CalendarSize;
  view?: CalendarView;
  /** 給了才畫出大中小與格/列那兩排鈕 —— 請假頁不需要它們。 */
  onSizeChange?: (size: CalendarSize) => void;
  onViewChange?: (view: CalendarView) => void;
  /**
   * 撐滿外框(首頁)。業主 2026-11-02:「首頁日曆不可出現垂直捲軸,載滿時應該調整
   * 格子的高度,萬不得已才出現捲軸」。格子不再是固定高度,而是把外框的高度分給
   * 每一週:事多的那一週自然高一點,空的那一週矮一點;真的塞不下才捲。
   */
  fill?: boolean;
}

export function LeaveCalendar({
  className = "",
  label = "休假日曆",
  size = "md",
  view = "grid",
  onSizeChange,
  onViewChange,
  fill = false,
}: LeaveCalendarProps) {
  const [cursor, setCursor] = useState<MonthCursor>(currentMonth);

  const { start, cells } = useMemo(() => gridOf(cursor), [cursor]);
  // 國定假日與六日的數字是紅字(業主 2026-10-15)。頭尾補滿的那幾天可能跨年,兩年都要
  const holidays = useTwHolidays([
    new Date(start * MS_PER_DAY).getUTCFullYear(),
    new Date((start + cells - 1) * MS_PER_DAY).getUTCFullYear(),
  ]);
  const from = isoOfEpochDay(start);
  const to = isoOfEpochDay(start + cells - 1);

  // 窗口就是畫面上看得到的那幾格,不多撈。整年一次撈回來的話,
  // 單子一多就是把整個資料庫畫到月曆上。
  const eventsQuery = useQuery({
    queryKey: ["calendar-events", from, to],
    queryFn: () => api.get<CalendarEvent[]>("/calendar/events", { from, to }),
  });

  // 假單跟著格子走,與上面那一支同一個窗口。key 的第一格仍然是 "leave",
  // 送出、核准、撤回之後的 invalidateQueries({ queryKey: ["leave"] }) 才吃得到它。
  const leaveQuery = useQuery({
    queryKey: ["leave", "window", from, to],
    queryFn: () => api.get<LeaveRequest[]>("/leave", { from, to }),
  });

  const events = Array.isArray(eventsQuery.data) ? eventsQuery.data : [];
  const leaves = Array.isArray(leaveQuery.data) ? leaveQuery.data : [];

  // 每一格那一天有哪些事。跨天的檔期與假單在它涵蓋的**每一天**都畫得到 ——
  // 只畫第一天的話,月曆翻到下一週那一條就整個消失,而事情還在進行中。
  const byDay = useMemo(() => {
    const map = new Map<number, { events: CalendarEvent[]; leaves: LeaveRequest[] }>();
    const slot = (epoch: number) => {
      const existing = map.get(epoch);
      if (existing) return existing;
      const fresh = { events: [] as CalendarEvent[], leaves: [] as LeaveRequest[] };
      map.set(epoch, fresh);
      return fresh;
    };

    for (const event of events) {
      const first = epochDay(event.start_date);
      const last = epochDay(event.end_date);
      if (first === null || last === null || last < first) continue;
      for (let epoch = Math.max(first, start); epoch <= Math.min(last, start + cells - 1); epoch += 1) {
        slot(epoch).events.push(event);
      }
    }

    for (const row of leaves) {
      if (!CALENDAR_STATUSES.includes(row.status)) continue;
      const first = epochDay(row.start_date);
      const last = epochDay(row.end_date);
      if (first === null || last === null || last < first) continue;
      for (let epoch = Math.max(first, start); epoch <= Math.min(last, start + cells - 1); epoch += 1) {
        slot(epoch).leaves.push(row);
      }
    }

    return map;
  }, [events, leaves, start, cells]);

  const title = `${cursor.year} 年 ${cursor.month} 月`;
  const todayEpoch = epochDay(todayIso());
  const weeks = Array.from({ length: cells / 7 }, (_, week) => week);

  return (
    <section
      aria-label={label}
      // 大小掛在 DOM 上:畫面上那三顆鈕與實際畫出來的東西是同一個來源,
      // 而測試也才守得住「選了小就真的變小」。
      data-size={size}
      data-view={view}
      data-fill={fill ? "true" : undefined}
      className={`flex min-w-0 flex-col gap-2 ${fill ? "h-full min-h-0" : ""} rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-3 ${className}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="上個月"
            onClick={() => setCursor((prev) => shiftMonth(prev, -1))}
            className="rounded-[var(--radius)] p-1.5 text-[var(--text-2)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]"
          >
            <ChevronLeft aria-hidden="true" size={18} />
          </button>
          <h3 className="m-0 min-w-[7.5rem] text-center text-base font-semibold text-[var(--text)]">{title}</h3>
          <button
            type="button"
            aria-label="下個月"
            onClick={() => setCursor((prev) => shiftMonth(prev, 1))}
            className="rounded-[var(--radius)] p-1.5 text-[var(--text-2)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]"
          >
            <ChevronRight aria-hidden="true" size={18} />
          </button>
          <button
            type="button"
            onClick={() => setCursor(currentMonth())}
            className="ml-1 rounded-[var(--radius)] px-2 py-1 text-xs text-[var(--text-2)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]"
          >
            回到本月
          </button>
        </div>

        {onSizeChange || onViewChange ? (
          <div className="flex flex-wrap items-center gap-1">
            {onSizeChange
              ? SIZE_LABEL.map((one) => (
                  <button
                    key={one.value}
                    type="button"
                    aria-pressed={size === one.value}
                    onClick={() => onSizeChange(one.value)}
                    className={
                      "rounded-[var(--radius)] border px-2 py-0.5 text-xs " +
                      (size === one.value
                        ? "border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--accent-hover)]"
                        : "border-[var(--border)] text-[var(--text-2)] hover:bg-[var(--surface-2)]")
                    }
                  >
                    {one.label}
                  </button>
                ))
              : null}
            {onViewChange
              ? ([
                  ["grid", "格"],
                  ["list", "列"],
                ] as [CalendarView, string][]).map(([value, text]) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={view === value}
                    onClick={() => onViewChange(value)}
                    className={
                      "rounded-[var(--radius)] border px-2 py-0.5 text-xs " +
                      (view === value
                        ? "border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--accent-hover)]"
                        : "border-[var(--border)] text-[var(--text-2)] hover:bg-[var(--surface-2)]")
                    }
                  >
                    {text}
                  </button>
                ))
              : null}
          </div>
        ) : null}

        {/* 圖例也帶文字。兩塊色塊不算說明,色盲與灰階列印都看不出差別。 */}
        <p className="m-0 flex flex-wrap items-center gap-3 text-xs text-[var(--text-3)]">
          <span className="inline-flex items-center gap-1">
            <Briefcase aria-hidden="true" size={12} className="text-[var(--accent)]" />
            專案檔期
          </span>
          <span className="inline-flex items-center gap-1">
            <UserRound aria-hidden="true" size={12} />
            同仁休假
          </span>
        </p>
      </div>

      {eventsQuery.error || leaveQuery.error ? (
        <p role="alert" className="m-0 text-sm text-[var(--danger)]">
          日曆載入失敗,請稍後再試。
        </p>
      ) : null}

      {view === "list" ? (
        <CalendarList byDay={byDay} start={start} cells={cells} />
      ) : (
      <div className={fill ? "min-h-0 w-full flex-1 overflow-auto" : "w-full overflow-x-auto"}>
        <table aria-label={`${title}的行事曆`}
          className={`w-full min-w-[40rem] table-fixed border-collapse ${fill ? "h-full" : ""}`}>
          <thead>
            <tr className="text-xs font-medium text-[var(--text-3)]">
              {WEEKDAYS.map((name) => (
                <th key={name} scope="col" className="px-1 py-1 text-center font-medium">
                  {name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week) => (
              <tr key={week}>
                {Array.from({ length: 7 }, (_, index) => {
                  const epoch = start + week * 7 + index;
                  const at = new Date(epoch * MS_PER_DAY);
                  const inMonth = at.getUTCFullYear() === cursor.year && at.getUTCMonth() + 1 === cursor.month;
                  const entries = byDay.get(epoch);
                  const isToday = epoch === todayEpoch;
                  const iso = at.toISOString().slice(0, 10);
                  const off = isHoliday(holidays, iso);
                  const offName = holidayName(holidays, iso);

                  return (
                    <td
                      key={epoch}
                      className={
                        `${fill ? FILL_CELL_HEIGHT : CELL_HEIGHT[size]} min-w-[5rem] border border-[var(--border)] p-1 align-top ` +
                        (inMonth ? "bg-[var(--surface)]" : "bg-[var(--surface-2)]")
                      }
                    >
                      <div
                        data-holiday={off ? "true" : undefined}
                        title={offName || undefined}
                        className={
                          "mb-1 text-xs " +
                          (isToday
                            ? "inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-[var(--accent)] px-1 font-semibold text-[var(--on-accent)]"
                            : off
                              ? "text-[var(--danger)]" + (inMonth ? "" : " opacity-60")
                              : inMonth
                                ? "text-[var(--text-2)]"
                                : "text-[var(--text-3)]")
                        }
                      >
                        {at.getUTCDate()}
                      </div>

                      {/* **小的那一種只畫數量。** 一格高的格子塞不下名字與單號,
                          硬塞出來的是一排看不懂的碎字。 */}
                      {size === "sm" ? (
                        countOf(entries) === 0 ? null : (
                          <span className="inline-block rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface-2)] px-1 text-[11px] text-[var(--text-2)]">
                            {`${countOf(entries)} 件`}
                          </span>
                        )
                      ) : (
                      <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
                        {/* 專案排在假單前面:業主要的是先看到公司那幾天有事。 */}
                        {/* 專案那一條是可以點開的:業主原話「日曆上每一格專案 滑鼠都要
                            可以點進去 然後懸浮視窗 出現該專案報價單」。卡片要的欄位
                            /api/calendar/events 已經一起帶回來了,點開不會再發一次請求。 */}
                        {(entries?.events ?? []).map((event) => (
                          <li
                            key={`quote-${event.quote_id}`}
                            data-quote-id={event.quote_id}
                            title={`${event.quote_no} ${event.title}(${formatEventRange(event)})`}
                            className="min-w-0"
                          >
                            <QuotePopover event={event} range={formatEventRange(event)} />
                          </li>
                        ))}

                        {(entries?.leaves ?? []).map((row) => {
                          const pending = row.status === "PENDING";
                          const mark = halfDayMark(
                            row,
                            epoch,
                            epochDay(row.start_date) ?? epoch,
                            epochDay(row.end_date) ?? epoch,
                          );
                          return (
                            <li
                              key={`leave-${row.id}`}
                              data-leave-id={row.id}
                              title={`${row.user_name} ${LEAVE_TYPE_SHORT_LABEL[row.leave_type]} ${formatLeaveRange(row)}(${LEAVE_STATUS_LABEL[row.status]})`}
                              className={
                                "flex min-w-0 flex-wrap items-center gap-1 rounded-[var(--radius)] border px-1 py-0.5 " +
                                "text-[11px] leading-tight text-[var(--text-2)] " +
                                (pending
                                  ? "border-dashed border-[var(--warn)] bg-[var(--surface)]"
                                  : "border-solid border-[var(--border-strong)] bg-[var(--surface-2)]")
                              }
                            >
                              <UserRound aria-hidden="true" size={11} className="shrink-0" />
                              <span className="truncate">{`${row.user_name} ${LEAVE_TYPE_SHORT_LABEL[row.leave_type]}`}</span>
                              {mark === "" ? null : <span className="shrink-0 text-[var(--text-3)]">{mark}</span>}
                              {/* 只標「待核准」;已核准的不寫(業主 2026-10-15)—— 日曆上的假本來就是請成了的 */}
                              {pending ? (
                                <span className="shrink-0 rounded px-1 text-[var(--warn)]">
                                  {LEAVE_STATUS_LABEL[row.status]}
                                </span>
                              ) : null}
                            </li>
                          );
                        })}
                      </ul>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </section>
  );
}

/** 一格那一天有幾件事。 */
function countOf(entries: DayEntries | undefined): number {
  return (entries?.events.length ?? 0) + (entries?.leaves.length ?? 0);
}

interface DayEntries {
  events: CalendarEvent[];
  leaves: LeaveRequest[];
}

/**
 * 「列」的那一種顯示方式:一天一列,只列有事的那幾天。
 *
 * **空的那幾天不列。** 一個月三十列、其中二十列寫著「沒有事」,
 * 要找的那幾天反而被埋掉 —— 而這個顯示方式存在的理由正是「快速看過有什麼」。
 */
function CalendarList({
  byDay,
  start,
  cells,
}: {
  byDay: Map<number, DayEntries>;
  start: number;
  cells: number;
}) {
  const days = Array.from({ length: cells }, (_, index) => start + index).filter(
    (epoch) => countOf(byDay.get(epoch)) > 0,
  );

  if (days.length === 0) {
    return <p className="m-0 px-1 py-4 text-sm text-[var(--text-3)]">這個月沒有排定的事。</p>;
  }

  return (
    <ul className="m-0 flex list-none flex-col gap-1 p-0">
      {days.map((epoch) => {
        const entries = byDay.get(epoch);
        const at = new Date(epoch * MS_PER_DAY);
        return (
          <li
            key={epoch}
            className="flex flex-wrap items-start gap-2 border-t border-[var(--border)] py-1.5 first:border-t-0"
          >
            <span className="tabular w-16 shrink-0 text-sm text-[var(--text-2)]">
              {`${at.getUTCMonth() + 1}/${at.getUTCDate()}`}
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              {(entries?.events ?? []).map((event) => (
                <span key={`quote-${event.quote_id}`} className="min-w-0 text-sm text-[var(--text)]">
                  {`${event.quote_no} ${event.title}`}
                </span>
              ))}
              {(entries?.leaves ?? []).map((row) => (
                <span key={`leave-${row.id}`} className="min-w-0 text-sm text-[var(--text-2)]">
                  {`休假 ${row.user_name} ${LEAVE_TYPE_SHORT_LABEL[row.leave_type]}${row.status === "PENDING" ? `(${LEAVE_STATUS_LABEL[row.status]})` : ""}`}
                </span>
              ))}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
